import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { User } from '@/lib/types'
import { getStoredUser, logout as apiLogout, isAuthenticated } from '@/api/auth'

interface AuthContextType {
  user: User | null
  isAdmin: boolean
  isLoading: boolean
  isAuthenticated: boolean
  setUser: (user: User | null) => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check for existing session on mount
    const storedUser = getStoredUser()
    if (storedUser && isAuthenticated()) {
      setUser(storedUser)
    }
    setIsLoading(false)
  }, [])

  const logout = useCallback(() => {
    apiLogout()
    setUser(null)
  }, [])

  const value: AuthContextType = {
    user,
    isAdmin: user?.role === 'admin',
    isLoading,
    isAuthenticated: !!user,
    setUser,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
